import { motion } from 'framer-motion';
import { Mail, MessageSquare, Check, Eye, MousePointer2, AlertTriangle, Send } from 'lucide-react';
import type { DeliveryStatus } from '@/lib/api';

interface SendRecord {
  id: string;
  medium: 'email' | 'text' | 'marked_by_user';
  destination: string;
  created_at: string;
  delivery_status: DeliveryStatus;
}

interface SendHistorySectionProps {
  records: SendRecord[];
  delay?: number;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getStatusDisplay(medium: string, status: DeliveryStatus): {
  text: string;
  icon: React.ReactNode;
  colorClass: string;
} {
  const mediumName = medium === 'email' ? 'Email' : medium === 'text' ? 'SMS' : '';

  if (medium === 'marked_by_user') {
    return {
      text: 'Marked as sent',
      icon: <Check className="h-4 w-4" />,
      colorClass: 'text-slate-600 dark:text-slate-400',
    };
  }

  switch (status) {
    case 'clicked':
      return {
        text: `${mediumName} was clicked`,
        icon: <MousePointer2 className="h-4 w-4" />,
        colorClass: 'text-green-600 dark:text-green-400',
      };
    case 'opened':
      return {
        text: `${mediumName} was opened`,
        icon: <Eye className="h-4 w-4" />,
        colorClass: 'text-blue-600 dark:text-blue-400',
      };
    case 'bounced':
      return {
        text: `${mediumName} bounced`,
        icon: <AlertTriangle className="h-4 w-4" />,
        colorClass: 'text-red-600 dark:text-red-400',
      };
    case 'delivered':
      return {
        text: `${mediumName} was delivered`,
        icon: medium === 'email' ? <Mail className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />,
        colorClass: 'text-slate-600 dark:text-slate-400',
      };
    default:
      // No status yet = just sent
      return {
        text: `${mediumName} sent`,
        icon: <Send className="h-4 w-4" />,
        colorClass: 'text-muted-foreground',
      };
  }
}

export function SendHistorySection({ records, delay = 0.4 }: SendHistorySectionProps) {
  if (!records || records.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="space-y-3"
    >
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Send History
      </h3>
      <div className="border rounded-lg divide-y">
        {records.map((record) => {
          const display = getStatusDisplay(record.medium, record.delivery_status);

          return (
            <div key={record.id} className="px-4 py-3">
              {/* Mobile: stacked layout, Desktop: side by side */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={display.colorClass}>
                    {display.icon}
                  </span>
                  <span className={`font-medium text-sm ${display.colorClass}`}>
                    {display.text}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 pl-6 sm:pl-0">
                  {record.destination && (
                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {record.destination}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground/70 whitespace-nowrap">
                    {formatDateTime(record.created_at)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
